import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getAllBlocks,
  getAllExtraCharges,
  getUnitPricingByBlockId,
  getUnitStatus
} from '../../services/api/implementation/impl';
import { useParams } from 'react-router-dom';
import { transformData } from '../../../super-admin/utils/transformData';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { createPortal } from 'react-dom';
import { ChevronDown, Info } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';

const SalesUnitInventoryList = () => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();
  const [newBlockId, setNewBlockId] = useState('');
  const [blocksDynaWithLevels, setBlocksDynaWithLevels] = useState([]);
  const page = 1;
  const limit = 20000;
  const [expandedRows, setExpandedRows] = useState([]);

  // Dropdown state for header portal dropdown
  const [openDropdown, setOpenDropdown] = useState(null);
  const blockHeaderRef = React.useRef(null);
  const [blockDropdownRect, setBlockDropdownRect] = useState(null);
  const statusHeaderRef = React.useRef(null);
  const [statusDropdownRect, setStatusDropdownRect] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('Select Status');
  const [selectedType, setSelectedType] = useState('Select Type');
  const [detailModalUnit, setDetailModalUnit] = useState(null);
  const [showExtraChargesBreakdown, setShowExtraChargesBreakdown] = useState(false);
  const typeHeaderRef = React.useRef(null);
  const [typeDropdownRect, setTypeDropdownRect] = useState(null);

  // === Queries ===
  const { data: blocksDyna } = useQuery({
    queryKey: ['blocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId
  });

  const {
    data: unitPricingData,
    refetch: refetchUnitPricingData
  } = useQuery({
    queryKey: ['unitPricing', newBlockId, page, selectedStatus],
    queryFn: () => getUnitPricingByBlockId(newBlockId, page, limit, selectedStatus),
    keepPreviousData: true,
    enabled: !!newBlockId
  });

  const { data: unitStatus } = useQuery({
    queryKey: ['unitStatus'],
    queryFn: () => getUnitStatus(),
    enabled: true
  });

  const { data: extraChargesByProject } = useQuery({
    queryKey: ['extraChargesByProject', projectId],
    queryFn: () => getAllExtraCharges(`project_id=${projectId}`),
    enabled: !!projectId
  });

  // === Effects ===

  // When project changes, reset block so data refetches for new project
  useEffect(() => {
    setNewBlockId('');
    setBlocksDynaWithLevels([]);
  }, [projectId]);

  useEffect(() => {
    mixpanel.track('Sales Unit Inventory List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (Array.isArray(blocksDyna) && blocksDyna.length > 0) {
      const blocksWithLevels = blocksDyna.filter((block) => block?.levels?.length > 0);
      const sorted = blocksWithLevels.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      if (sorted.length > 0) {
        setBlocksDynaWithLevels(sorted);
        setNewBlockId((prev) => (prev ? prev : sorted[0].id));
      }
    }
  }, [blocksDyna]);

  useEffect(() => {
    if (newBlockId) {
      refetchUnitPricingData();
    }
  }, [newBlockId, refetchUnitPricingData]);

  // Plot = Independent + no built-up area; House = Independent + built-up area; Apartment = Group
  const getUnitType = (item) => {
    const acc = item?.accommodation;
    const accType = (acc?.accomodationType || acc?.accommodationType || '').toUpperCase();
    const buildUp = Number(acc?.buildUpArea ?? item?.buildUpArea ?? 0);
    if (accType === 'GROUP') return 'Apartment';
    if (accType === 'INDEPENDENT') return buildUp > 0 ? 'House' : 'Plot';
    return buildUp > 0 ? 'House' : 'Plot';
  };

  // === Data transformations ===
  const UnitPricingFormat = [
    'id',
    'unitName',
    'cost',
    'plc_price',
    'gst',
    'extra_charges',
    'total_price',
    'accommodationName',
    'unitType',
    'status'
  ];

  const RequiredUnitPricingDataRaw =
    unitPricingData?.data && unitPricingData?.data.length > 0
      ? unitPricingData.data
          .map((item) =>
            transformData(
              {
                ...item,
                cost: parseInt(item?.cost) || 0,
                plc_price: parseInt(item?.plc_price) || 0,
                gst: `₹${(parseInt(item?.gst) || 0).toLocaleString('en-IN')}.00`,
                extra_charges: `₹${(parseInt(item.extra_charges) || 0).toLocaleString('en-IN')}.00`,
                total_price: `₹${(
                  (parseInt(item?.cost) || 0) +
                  (parseInt(item?.plc_price) || 0) +
                  (parseInt(item?.gst) || 0)
                ).toLocaleString('en-IN')}.00`,
                accommodationName: item?.accommodation?.name,
                unitType: getUnitType(item),
                status: item?.status
              },
              UnitPricingFormat
            )
          )
          .sort((a, b) => a.id - b.id)
      : [];

  const RequiredUnitPricingData =
    selectedType === 'Select Type'
      ? RequiredUnitPricingDataRaw
      : RequiredUnitPricingDataRaw.filter((row) => row.unitType === selectedType);

  // === Handlers ===
  const handleToggleExpand = (unitId) => {
    setExpandedRows((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]
    );
  };

  const currentBlockName =
    blocksDynaWithLevels?.find((b) => String(b.id) === String(newBlockId))?.name || '---';

  const PortalDropdown = ({ anchorRect, children }) => {
    if (!anchorRect) return null;
    return createPortal(
      <div
        className="absolute bg-white border rounded shadow z-[1000]"
        style={{
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom,
          minWidth: anchorRect.width,
          maxHeight: '250px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>,
      document.body
    );
  };

  const handleHeaderDropdownClick = (type) => {
    const next = openDropdown === type ? null : type;
    setOpenDropdown(next);
    if (next === 'block' && blockHeaderRef?.current) {
      setBlockDropdownRect(blockHeaderRef.current.getBoundingClientRect());
    }
    if (next === 'status' && statusHeaderRef?.current) {
      setStatusDropdownRect(statusHeaderRef.current.getBoundingClientRect());
    }
    if (next === 'type' && typeHeaderRef?.current) {
      setTypeDropdownRect(typeHeaderRef.current.getBoundingClientRect());
    }
  };

  const handleCloseDropdown = () => {
    setOpenDropdown(null);
    setBlockDropdownRect(null);
    setStatusDropdownRect(null);
    setTypeDropdownRect(null);
  };

  const handleSelectDropdownItemFromHeader = (blockId) => {
    setNewBlockId(blockId);
    handleCloseDropdown();
  };

  const handleSelectStatusFromHeader = (status) => {
    setSelectedStatus(status);
    handleCloseDropdown();
  };

  const handleSelectTypeFromHeader = (type) => {
    setSelectedType(type);
    handleCloseDropdown();
  };

  const formatCurrency = (val) => {
    const n = Number(val);
    return isNaN(n) ? '—' : `₹${n.toLocaleString('en-IN')}`;
  };

  // Extra charges breakdown for current unit: filter by accommodation id
  const extraChargesBreakdown = React.useMemo(() => {
    const list = extraChargesByProject || [];
    const accommodationId = detailModalUnit?.accommodation?.id;
    if (!accommodationId || !Array.isArray(list)) return [];
    return list
      .map((charge) => {
        const accomodations = charge?.accomodations || [];
        const matched = accomodations.find(
          (a) => Number(a?.accomodation_id) === Number(accommodationId)
        );
        if (!matched) return null;
        return {
          name: charge?.name || '—',
          total_price: Number(matched?.total_price) || 0
        };
      })
      .filter(Boolean);
  }, [extraChargesByProject, detailModalUnit?.accommodation?.id]);

  return (
    <>
      {detailModalUnit && (
        <Modal
          width={50}
          name="Unit Details"
          onClose={() => {
            setDetailModalUnit(null);
            setShowExtraChargesBreakdown(false);
          }}>
          <div className="space-y-5">
            {/* Basic info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Unit Name</p>
                <p className="text-gray-900 font-medium">{detailModalUnit.unitName || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</p>
                <p className="text-gray-900 font-medium">{detailModalUnit.status || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Type</p>
                <p className="text-gray-900 font-medium">{getUnitType(detailModalUnit) || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Block</p>
                <p className="text-gray-900">{detailModalUnit.block?.name || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Level / Floor</p>
                <p className="text-gray-900">{detailModalUnit.level?.name ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Project</p>
                <p className="text-gray-900">{detailModalUnit.project?.name || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Accommodation</p>
                <p className="text-gray-900">{detailModalUnit.accommodation?.name || '—'}</p>
              </div>
              {/* Area by type: Plot = plot area only; House = plot + buildup + carpet; Apartment (Group) = buildup + carpet only */}
              {(() => {
                const unitType = getUnitType(detailModalUnit);
                const acc = detailModalUnit.accommodation;
                const plotArea = Number(acc?.plotArea ?? detailModalUnit.area ?? 0);
                const buildUpArea = Number(acc?.buildUpArea ?? detailModalUnit.buildUpArea ?? 0);
                const carpetArea = Number(acc?.carpetArea ?? detailModalUnit.carpetArea ?? 0);
                const unit = acc?.measurementUnit || 'sq/ft';
                if (unitType === 'Plot' && plotArea > 0) {
                  return (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Plot area</p>
                      <p className="text-gray-900">{plotArea.toLocaleString('en-IN')} {unit}</p>
                    </div>
                  );
                }
                if (unitType === 'House') {
                  return (
                    <>
                      {plotArea > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Plot area</p>
                          <p className="text-gray-900">{plotArea.toLocaleString('en-IN')} {unit}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Build-up area</p>
                        <p className="text-gray-900">{(buildUpArea || 0).toLocaleString('en-IN')} {unit}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Carpet area</p>
                        <p className="text-gray-900">{(carpetArea || 0).toLocaleString('en-IN')} {unit}</p>
                      </div>
                    </>
                  );
                }
                if (unitType === 'Apartment') {
                  return (
                    <>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Build-up area</p>
                        <p className="text-gray-900">{(buildUpArea || 0).toLocaleString('en-IN')} {unit}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Carpet area</p>
                        <p className="text-gray-900">{(carpetArea || 0).toLocaleString('en-IN')} {unit}</p>
                      </div>
                    </>
                  );
                }
                return null;
              })()}
            </div>

            {/* Pricing */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">Pricing</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cost</span>
                  <span className="font-medium">{formatCurrency(detailModalUnit.cost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PLC price</span>
                  <span className="font-medium">{formatCurrency(detailModalUnit.plc_price)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">GST</span>
                  <span className="font-medium">{formatCurrency(detailModalUnit.gst)}</span>
                </div>
                <div className="flex justify-between col-span-full border-t pt-2 mt-1">
                  <span className="text-gray-800 font-medium">Total price</span>
                  <span className="font-semibold">
                    {formatCurrency(
                      (Number(detailModalUnit.cost) || 0) +
                        (Number(detailModalUnit.plc_price) || 0) +
                        (Number(detailModalUnit.gst) || 0)
                    )}
                  </span>
                </div>
                <div className="flex justify-between col-span-full items-center">
                  <span className="text-gray-600 flex items-center gap-1.5">
                    Extra charges
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowExtraChargesBreakdown((prev) => !prev);
                      }}
                      className={`inline-flex items-center justify-center w-5 h-5 rounded-full transition-colors ${
                        showExtraChargesBreakdown ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800'
                      }`}
                      aria-label="Extra charges breakdown">
                      <Info size={14} />
                    </button>
                  </span>
                  <span className="font-medium">{formatCurrency(detailModalUnit.extra_charges)}</span>
                </div>
                {showExtraChargesBreakdown && (
                  <div className="col-span-full pl-2 pt-1 pb-1 border-l-2 border-gray-200 ml-1 space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Breakdown</p>
                    {extraChargesBreakdown.length === 0 ? (
                      <p className="text-sm text-gray-500">No extra charges for this accommodation.</p>
                    ) : (
                      <>
                        <div className="flex justify-between text-xs font-medium text-gray-500 mb-1">
                          <span>Name</span>
                          <span>Total price</span>
                        </div>
                        {extraChargesBreakdown.map((row, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-700">{row.name}</span>
                            <span className="font-medium">{formatCurrency(row.total_price)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm border-t border-gray-200 pt-1 mt-1 font-medium">
                          <span>Total</span>
                          <span>{formatCurrency(extraChargesBreakdown.reduce((s, r) => s + r.total_price, 0))}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <div className="flex justify-between col-span-full border-t pt-2 mt-1">
                  <span className="text-gray-800 font-medium">Final price</span>
                  <span className="font-semibold">
                    {formatCurrency(
                      (Number(detailModalUnit.cost) || 0) +
                        (Number(detailModalUnit.plc_price) || 0) +
                        (Number(detailModalUnit.gst) || 0) +
                        (Number(detailModalUnit.extra_charges) || 0)
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Closed deal */}
            {detailModalUnit.closedDeal?.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-green-700 mb-2">Closed deal</p>
                <div className="bg-green-50 rounded-lg p-4 space-y-2 text-sm">
                  {detailModalUnit.closedDeal.map((cd) => (
                    <div key={cd.id} className="flex justify-between flex-wrap gap-2">
                      <span>Total price: {formatCurrency(cd.total_price)}</span>
                      <span>Due: {formatCurrency(cd.due_price)}</span>
                      <span>Status: {cd.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Under consideration */}
            {detailModalUnit.underConsideration?.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-blue-700 mb-2">Under consideration</p>
                <div className="bg-blue-50 rounded-lg p-4 space-y-2 text-sm">
                  {detailModalUnit.underConsideration.map((uc) => (
                    <div key={uc.id} className="flex justify-between flex-wrap gap-2">
                      <span>Proposed: {formatCurrency(uc.unit_price || uc.customer_proposal)}</span>
                      <span>{uc.approved ? 'Approved' : 'Pending'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      <div
        className={`transition-all duration-300 mt-3 ease-in-out w-full flex flex-col pl-5 pr-5 bg-gray-100 overflow-hidden`}>
      <div className="table-container mt-5 h-[calc(100vh-13rem)]  overflow-auto">
        <style>{`
          .table-container .table-body {
            position: static !important;
            overflow-y: visible !important;
            max-height: none !important;
            height: auto !important;
            top: auto !important;
            padding-bottom: 0 !important;
          }
          .table-container .table-container {
            height: auto !important;
          }
          .table-container > div {
            height: auto !important;
          }
        `}</style>
        <div className="overflow-scroll hide-scrollbar w-full relative rounded-t-lg ">
          <table className=" text-left w-full ">
            <thead className=" bg-gray-200 !text-gray-600 w-full sticky top-0 z-20">
              <tr className="font-normal h-[3rem] w-full">
                <th className="px-4 py-2 border-b font-normal relative" ref={blockHeaderRef}>
                  <span>Block Name</span>
                  <button
                    className="ml-1 align-middle"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHeaderDropdownClick('block');
                    }}>
                    <ChevronDown size={16} />
                  </button>
                  {openDropdown === 'block' && blockDropdownRect && (
                    <PortalDropdown anchorRect={blockDropdownRect}>
                      {blocksDynaWithLevels?.map((block) => (
                        <div
                          key={block.id}
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            String(block.id) === String(newBlockId) ? 'bg-blue-100' : ''
                          }`}
                          onClick={() => handleSelectDropdownItemFromHeader(block.id)}>
                          {block.name}
                        </div>
                      ))}
                    </PortalDropdown>
                  )}
                </th>
                <th className="px-4 py-2 border-b font-normal ">Unit Name</th>
                <th className="px-4 py-2 border-b font-normal relative" ref={typeHeaderRef}>
                  <span>Type{selectedType !== 'Select Type' ? ` (${selectedType})` : ''}</span>
                  <button
                    type="button"
                    className="ml-1 align-middle inline-flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHeaderDropdownClick('type');
                    }}
                    aria-label="Filter by type">
                    <ChevronDown size={16} />
                  </button>
                  {openDropdown === 'type' && typeDropdownRect && (
                    <PortalDropdown anchorRect={typeDropdownRect}>
                      {['Select Type', 'Plot', 'House', 'Apartment'].map((t) => (
                        <div
                          key={t}
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            selectedType === t ? 'bg-blue-100' : ''
                          }`}
                          onClick={() => handleSelectTypeFromHeader(t)}>
                          {t}
                        </div>
                      ))}
                    </PortalDropdown>
                  )}
                </th>
                <th className="px-4 py-2 border-b font-normal">Unit Price</th>
                <th className="px-4 py-2 border-b font-normal">Total Plc Price</th>
                <th className="px-4 py-2 border-b font-normal">GST</th>
                <th className="px-4 py-2 border-b font-normal">Total Price</th>
                <th className="px-4 py-2 border-b font-normal">Extra Charges</th>
                <th className="px-4 py-2 border-b font-normal">Discount</th>
                <th className="px-4 py-2 border-b font-normal">Acc Name</th>
                <th className="px-4 py-2 border-b font-normal relative" ref={statusHeaderRef}>
                  <span>Status</span>
                  <button
                    className="ml-1 align-middle"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHeaderDropdownClick('status');
                    }}>
                    <ChevronDown size={16} />
                  </button>
                  {openDropdown === 'status' && statusDropdownRect && (
                    <PortalDropdown anchorRect={statusDropdownRect}>
                      <div
                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                          selectedStatus === 'Select Status' ? 'bg-blue-100' : ''
                        }`}
                        onClick={() => handleSelectStatusFromHeader('Select Status')}>
                        Select Status
                      </div>
                      {unitStatus?.map((s) => (
                        <div
                          key={s}
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            selectedStatus === s ? 'bg-blue-100' : ''
                          }`}
                          onClick={() => handleSelectStatusFromHeader(s)}>
                          {s}
                        </div>
                      ))}
                    </PortalDropdown>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {RequiredUnitPricingData && RequiredUnitPricingData.length > 0 ? (
                RequiredUnitPricingData.map((row, idx) => {
                  const original = unitPricingData?.data?.find((u) => u.id === row.id);
                  const hasExpandable =
                    original?.underConsideration?.length > 0 ||
                    original?.closedDeal?.length > 0 ||
                    original?.pricingHistory?.length > 1;
                  return (
                    <React.Fragment key={row.id || idx}>
                      <tr
                        className="border-b hover:bg-gray-50 bg-white h-[3.5rem] cursor-pointer"
                        onClick={() => setDetailModalUnit(original)}>
                        <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                          {idx === 0 ? currentBlockName : ''}
                        </td>
                        <td className="px-4 py-2">
                          {hasExpandable && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleExpand(row.id);
                              }}
                              style={{ marginRight: 8 }}>
                              {expandedRows.includes(row.id) ? '-' : '+'}
                            </button>
                          )}
                          {row.unitName || '---'}
                        </td>
                        <td className="px-4 py-2">{row.unitType || '---'}</td>
                        <td className="px-4 py-2">{row.cost || '---'}</td>
                        <td className="px-4 py-2">{row.plc_price || '---'}</td>
                        <td className="px-4 py-2">{row.gst || '---'}</td>
                        <td className="px-4 py-2">{row.total_price || '---'}</td>
                        <td className="px-4 py-2">{row.extra_charges || '---'}</td>
                        <td className="px-4 py-2">---</td>
                        <td className="px-4 py-2">{row.accommodationName || '---'}</td>
                        <td className="px-4 py-2">{row.status || '---'}</td>
                      </tr>
                      {/* Pricing History Sub-rows */}
                      {expandedRows.includes(row.id) &&
                        original?.pricingHistory?.length > 1 &&
                        original.pricingHistory.slice(1)?.map((ph, i) => {
                          const cost = Number(ph.cost) || 0;
                          const plcPrice = Number(ph.plc_price) || 0;
                          const gst = Number(ph.gst) || 0;
                          const totalPrice = cost + plcPrice + gst;
                          return (
                            <tr key={ph.id || `ph-${i}`} className="bg-amber-50 mb-2">
                              <td className="px-4 py-2 font-semibold text-amber-800">
                                Old Price v{i + 1}
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">
                                {cost > 0 ? `₹${cost.toLocaleString('en-IN')}` : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {plcPrice > 0 ? `₹${plcPrice.toLocaleString('en-IN')}` : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {gst > 0 ? `₹${gst.toLocaleString('en-IN')}` : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {totalPrice > 0
                                  ? `₹${totalPrice.toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {ph.extra_charges
                                  ? `₹${Number(ph.extra_charges).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">---</td>
                            </tr>
                          );
                        })}
                      {/* Under Consideration Sub-row */}
                      {expandedRows.includes(row.id) &&
                        original?.underConsideration?.length > 0 &&
                        original.underConsideration.map((uc, i) => (
                          <tr key={uc.id || i} className="bg-blue-50 mb-2">
                            <td className="px-4 py-2 font-semibold text-blue-800">
                              Under Consideration
                            </td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">
                              {uc.unit_price || uc.customer_proposal
                                ? `₹${Number(uc.unit_price || uc.customer_proposal).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {uc.plc_price
                                ? `₹${Number(uc.plc_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {uc.gst_price || uc.gst
                                ? `₹${Number(uc.gst_price || uc.gst).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {uc.total_price
                                ? `₹${Number(uc.total_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {uc.extra_charges
                                ? `₹${Number(uc.extra_charges).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {uc.discount
                                ? `₹${Number(uc.discount).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">
                              {uc.approved ? 'Approved' : 'Approval' || '---'}
                            </td>
                          </tr>
                        ))}
                      {/* Deal Closed Sub-row */}
                      {expandedRows.includes(row.id) &&
                        original?.closedDeal?.length > 0 &&
                        original.closedDeal?.map((cd, i) => (
                          <tr key={cd.id || i} className="bg-green-50 mb-2">
                            <td className="px-4 py-2 font-semibold text-green-800">
                              Deal Closed
                            </td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">
                              {cd.unit_price
                                ? `₹${Number(cd.unit_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {cd.plc_price
                                ? `₹${Number(cd.plc_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {cd.gst_price
                                ? `₹${Number(cd.gst_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {cd.total_price
                                ? `₹${Number(cd.total_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {cd.extra_charges
                                ? `₹${Number(cd.extra_charges).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {cd.discount
                                ? `₹${Number(cd.discount).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">{cd.status || 'N/A'}</td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11} className="text-center py-10 align-middle">
                    <div className="flex flex-col items-center justify-center w-full h-full">
                      <h2 className="text-lg font-bold text-gray-800 mb-1">No Data Found</h2>
                      <p className="text-gray-600 text-sm">
                        The page you are trying to access has no data.
                        <br />
                        Proceed with adding data.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </>
  );
};

export default SalesUnitInventoryList;
